import { useState } from "react";

import { NvAlertDialog, NvButton, NvEmptyState, NvErrorState } from "@/components/navigator";
import { useRequest } from "@/hooks/use-request";
import { getApiError } from "@/lib/api-error";
import { listMarks, setMark, type FinderCard } from "@/services/navigator/finder-service";
import { listRecentAgents } from "@/services/navigator/my-page-service";

import { MyAgentCard, MyStarButton } from "./my-agent-card";
import { AlertPopup, CARD_GRID, CardSkeletons, FinderLink, RETRY_LATER } from "./my-card-parts";
import { MyPageHeader, useMyPage } from "./my-page-shared";

// My Page 본문 (Figma ver.7 "08 My Page": 북마크 847:47765 · 비교함 847:47855 · 최근 조회 847:57463 · 문의 이력 847:47968 · 문의 상세 847:48059 · 847:51593).
// 카드는 4열(282 · 간격 24, MyAgentCard), 팝업(해제 확인 · 비교 안내 · 비교표)은 설계서 v0.5.
// 비교함은 my-compare.tsx, 문의 이력 · 상세는 my-inquiries.tsx (라우터는 여기서 한꺼번에 가져가요)
export { MyCompareSection } from "./my-compare";
export { MyInquiriesSection, MyInquiryDetailSection } from "./my-inquiries";

// ── 북마크 ─────────────────────────────────────────────────────────────

export function MyBookmarksSection() {
  const { refresh } = useMyPage();
  const { data, error, loading, reload, setData } = useRequest((signal) => listMarks("bookmarks", signal), []);
  const [alert, setAlert] = useState<string | null>(null);
  // 해제 확인 팝업 (설계서 v0.4 "북마크를 해제 하시겠습니까?")
  const [confirm, setConfirm] = useState<(FinderCard & { added_at: string | null }) | null>(null);
  const items = data?.items ?? null;
  const limit = data?.limit ?? 20;
  const remove = async (card: FinderCard & { added_at: string | null }) => {
    setConfirm(null);
    setData((d) => d && { ...d, items: d.items.filter((c) => c.id !== card.id) });
    try {
      await setMark("bookmarks", card.id, false);
      refresh();
    } catch (err) {
      setData((d) => d && { ...d, items: [card, ...d.items] });
      setAlert(getApiError(err)?.message ?? RETRY_LATER);
    }
  };
  return (
    <>
      <MyPageHeader title="북마크한 에이전트" description={`${items?.length ?? 0}개 · 최대 ${limit}개까지 저장할 수 있어요`} />
      {!!error && <NvErrorState error={error} onRetry={reload} />}
      {loading && !data && <CardSkeletons />}
      {items && items.length === 0 && <NvEmptyState action={<FinderLink />}>북마크한 에이전트가 없어요.</NvEmptyState>}
      <div className={CARD_GRID}>
        {items?.map((card) => (
          <MyAgentCard key={card.id} card={card} action={<MyStarButton on onToggle={() => setConfirm(card)} />} />
        ))}
      </div>
      <NvAlertDialog
        open={confirm !== null}
        onOpenChange={(o) => !o && setConfirm(null)}
        message="북마크를 해제 하시겠습니까?"
        actions={
          <>
            <NvButton variant="secondary" onClick={() => setConfirm(null)}>
              취소
            </NvButton>
            <NvButton onClick={() => confirm && remove(confirm)}>확인</NvButton>
          </>
        }
      />
      <AlertPopup message={alert} onClose={() => setAlert(null)} />
    </>
  );
}

// ── 최근 조회 ──────────────────────────────────────────────────────────

export function MyRecentSection() {
  const { refresh } = useMyPage();
  const { data, error, loading, reload, setData } = useRequest((signal) => listRecentAgents(signal), []);
  const [alert, setAlert] = useState<string | null>(null);
  const toggle = async (card: FinderCard, next: boolean) => {
    const patch = (on: boolean) => setData((d) => d && { ...d, items: d.items.map((c) => (c.id === card.id ? { ...c, is_bookmarked: on } : c)) });
    patch(next);
    try {
      await setMark("bookmarks", card.id, next);
      refresh();
    } catch (err) {
      patch(!next);
      setAlert(getApiError(err)?.message ?? RETRY_LATER);
    }
  };
  return (
    <>
      <MyPageHeader title="최근 조회한 에이전트" description={`최근 조회순 · 최대 ${data?.limit ?? 20}개 (같은 에이전트는 최신 1건만 유지)`} />
      {!!error && <NvErrorState error={error} onRetry={reload} />}
      {loading && !data && <CardSkeletons />}
      {data && data.items.length === 0 && <NvEmptyState action={<FinderLink />}>최근 30일 동안 조회한 에이전트가 없어요.</NvEmptyState>}
      <div className={CARD_GRID}>
        {data?.items.map((card) => (
          <MyAgentCard key={card.id} card={card} action={<MyStarButton on={!!card.is_bookmarked} onToggle={() => toggle(card, !card.is_bookmarked)} />} />
        ))}
      </div>
      <AlertPopup message={alert} onClose={() => setAlert(null)} />
    </>
  );
}
